import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ConfirmationConfig, ConfirmationResult, HeaderInfo, SystemAlert } from "../interfaces/layout.interface";
import { User } from "../../auth/interfaces/user.interface";
import { UserStats } from "../../auth/interfaces/user-stats.interface";
import { AuthService } from "../../auth/services/auth.service";
import { tap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class LayoutService {
  private headerInfoSubject = new BehaviorSubject<HeaderInfo>({
    title: "",
    subtitle: "",
    breadcrumbs: [],
  });

  private userInfoSubject = new BehaviorSubject<User>({
    uniqueId: "1",
    fullName: "Mohammed Fathauddin",
    email: "mohammed.fathauddin@turbocode.in",
    isNewUser: false,
    isVerified: true,
    isProfileCompleted: true,
    profileImage: "https://i.pravatar.cc/150?u=mohammed",
    completionPercentage: 85,
  });

  private systemAlertSubject = new BehaviorSubject<SystemAlert>({
    message: "",
    type: "info",
    visible: false,
  });

  private userStatsSubject = new BehaviorSubject<UserStats | null>(null);

  private profileCompletionModalSubject = new BehaviorSubject<boolean>(false);
  private showInstitutionCreationModalSubject = new BehaviorSubject<boolean>(
    false,
  );
  private showCreateBatchModalSubject = new BehaviorSubject<boolean>(false);
  private showCareerCreationModalSubject = new BehaviorSubject<boolean>(false);
  private showUserCreationModalSubject = new BehaviorSubject<boolean>(false);
  private showStaffCreationModalSubject = new BehaviorSubject<boolean>(false);
  private showConfigCreationModalSubject = new BehaviorSubject<boolean>(false);
  private showAllocationModalSubject = new BehaviorSubject<boolean>(false);
  private showTeamModalSubject = new BehaviorSubject<boolean>(false);
  private confirmationSubject = new Subject<{
    config: ConfirmationConfig;
    resolver: (result: boolean) => void;
  }>();

  private refreshDataSubject = new Subject<string>();

  private selectedInstitutionToEditSubject = new BehaviorSubject<any | null>(null);
  private selectedCareerToEditSubject = new BehaviorSubject<any | null>(null);
  private selectedUserToEditSubject = new BehaviorSubject<any | null>(null);
  private selectedInstitutionForStaffSubject = new BehaviorSubject<string | undefined>(undefined);
  private selectedConfigToEditSubject = new BehaviorSubject<any | null>(null);
  private selectedConfigCategorySubject = new BehaviorSubject<string>('');
  private selectedAllocationToEditSubject = new BehaviorSubject<any | null>(null);
  private selectedTeamToEditSubject = new BehaviorSubject<any | null>(null);

  // Observables
  headerInfo$ = this.headerInfoSubject.asObservable();
  userInfo$ = this.userInfoSubject.asObservable();
  userStats$ = this.userStatsSubject.asObservable();
  systemAlert$ = this.systemAlertSubject.asObservable();
  profileCompletionModal$ = this.profileCompletionModalSubject.asObservable();
  showInstitutionCreationModal$ =
    this.showInstitutionCreationModalSubject.asObservable();
  showCreateBatchModal$ = this.showCreateBatchModalSubject.asObservable();
  showCareerCreationModal$ = this.showCareerCreationModalSubject.asObservable();
  showUserCreationModal$ = this.showUserCreationModalSubject.asObservable();
  showStaffCreationModal$ = this.showStaffCreationModalSubject.asObservable();
  showConfigCreationModal$ = this.showConfigCreationModalSubject.asObservable();
  selectedInstitutionToEdit$ = this.selectedInstitutionToEditSubject.asObservable();
  selectedCareerToEdit$ = this.selectedCareerToEditSubject.asObservable();
  selectedUserToEdit$ = this.selectedUserToEditSubject.asObservable();
  selectedInstitutionForStaff$ = this.selectedInstitutionForStaffSubject.asObservable();
  selectedConfigToEdit$ = this.selectedConfigToEditSubject.asObservable();
  selectedConfigCategory$ = this.selectedConfigCategorySubject.asObservable();
  showAllocationModal$ = this.showAllocationModalSubject.asObservable();
  selectedAllocationToEdit$ = this.selectedAllocationToEditSubject.asObservable();
  showTeamModal$ = this.showTeamModalSubject.asObservable();
  selectedTeamToEdit$ = this.selectedTeamToEditSubject.asObservable();
  confirmation$ = this.confirmationSubject.asObservable();
  refreshData$ = this.refreshDataSubject.asObservable();

  constructor(private authService: AuthService) { }

  fetchUserStats(): Observable<UserStats> {
    return this.authService
      .currentUserStats()
      .pipe(tap((stats) => this.setUserStats(stats)));
  }

  fetchUserInfo(): Observable<User> {
    return this.authService
      .currentUser()
      .pipe(tap((user) => this.setUserInfo(user)));
  }

  // Header Getters/Setters
  setHeaderInfo(info: Partial<HeaderInfo>) {
    this.headerInfoSubject.next({ ...this.headerInfoSubject.value, ...info });
  }

  getHeaderInfo(): HeaderInfo {
    return this.headerInfoSubject.value;
  }

  // User Getters/Setters
  setUserInfo(info: Partial<User>) {
    this.userInfoSubject.next({ ...this.userInfoSubject.value, ...info });
  }

  getUserInfo(): User {
    console.log(this.userInfoSubject.value);
    return this.userInfoSubject.value;
  }

  // User Stats Getters/Setters
  setUserStats(stats: UserStats) {
    this.userStatsSubject.next(stats);
  }

  getUserStats(): UserStats | null {
    return this.userStatsSubject.value;
  }

  // System Alert Getters/Setters
  setSystemAlert(alert: Partial<SystemAlert>) {
    this.systemAlertSubject.next({
      ...this.systemAlertSubject.value,
      ...alert,
    });
  }

  getSystemAlert(): SystemAlert {
    return this.systemAlertSubject.value;
  }

  hideSystemAlert() {
    this.systemAlertSubject.next({
      ...this.systemAlertSubject.value,
      visible: false,
    });
  }

  // Profile Completion Modal
  showProfileCompletionModal() {
    this.profileCompletionModalSubject.next(true);
  }

  hideProfileCompletionModal() {
    this.profileCompletionModalSubject.next(false);
  }

  // Institution Creation Modal
  showInstitutionCreationModal(institution: any = null) {
    this.selectedInstitutionToEditSubject.next(institution);
    this.showInstitutionCreationModalSubject.next(true);
  }

  hideInstitutionCreationModal() {
    this.showInstitutionCreationModalSubject.next(false);
    this.selectedInstitutionToEditSubject.next(null);
  }

  // Create Batch Modal
  showCreateBatchModal() {
    this.showCreateBatchModalSubject.next(true);
  }

  hideCreateBatchModal() {
    this.showCreateBatchModalSubject.next(false);
  }

  // Career Creation Modal
  showCareerCreationModal(career: any = null) {
    this.selectedCareerToEditSubject.next(career);
    this.showCareerCreationModalSubject.next(true);
  }

  hideCareerCreationModal() {
    this.showCareerCreationModalSubject.next(false);
    this.selectedCareerToEditSubject.next(null);
  }

  // User Creation Modal
  showUserCreationModal(user: any = null) {
    this.selectedUserToEditSubject.next(user);
    this.showUserCreationModalSubject.next(true);
  }

  hideUserCreationModal() {
    this.showUserCreationModalSubject.next(false);
    this.selectedUserToEditSubject.next(null);
  }

  // Staff Creation Modal
  showStaffCreationModal(institutionId?: string) {
    this.selectedInstitutionForStaffSubject.next(institutionId);
    this.showStaffCreationModalSubject.next(true);
  }

  hideStaffCreationModal() {
    this.showStaffCreationModalSubject.next(false);
    this.selectedInstitutionForStaffSubject.next(undefined);
  }

  // Config Creation Modal
  showConfigCreationModal(category: string, config: any = null) {
    this.selectedConfigCategorySubject.next(category);
    this.selectedConfigToEditSubject.next(config);
    this.showConfigCreationModalSubject.next(true);
  }

  hideConfigCreationModal() {
    this.showConfigCreationModalSubject.next(false);
    this.selectedConfigToEditSubject.next(null);
  }

  // Allocation Modal
  showAllocationModal(allocation: any = null) {
    this.selectedAllocationToEditSubject.next(allocation);
    this.showAllocationModalSubject.next(true);
  }

  hideAllocationModal() {
    this.showAllocationModalSubject.next(false);
    this.selectedAllocationToEditSubject.next(null);
  }

  showTeamModal(team: any = null) {
    console.log(team);
    this.selectedTeamToEditSubject.next(team);
    this.showTeamModalSubject.next(true);
  }

  hideTeamModal() {
    this.showTeamModalSubject.next(false);
    this.selectedTeamToEditSubject.next(null);
  }

  // Institution Edit
  showEditInstitutionModal(institution: any) {
    this.showInstitutionCreationModal(institution);
  }

  confirm(config: ConfirmationConfig): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationSubject.next({
        config,
        resolver: resolve,
      });
    });
  }

  refreshData(type: 'users' | 'institutions' | 'staff' | 'batches' | 'careers' | 'students' | 'configs' | 'allocations') {
    this.refreshDataSubject.next(type);
  }
}
